import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Button } from "antd";
import { LockOutlined, ReloadOutlined, SaveOutlined } from "@ant-design/icons";
import DetailDrawer from "../../../components/shared/DetailDrawer";
import { updatePatient, activatePatient, deactivatePatient } from "../../../api/healthcare/PatientApi";
import { updateUser, activateUser, deactivateUser } from "../../../api/admin/userApi";
import { Customer } from "../../../types/inventory/CustomerTypes";
import { useNotifications } from "../../../context/NotificationContext";
import ResetPasswordModal from "../../healthcare/patient/modalComponents/submodals/ResetPasswordModal";
import OrdersTab from "../../healthcare/patient/modalComponents/OrdersTab";
import CustomerInfoStrip from "./ModalTabs/CustomerInfoStrip";
import CustomerOverviewTab from "./ModalTabs/CustomerOverviewTab";
import CustomerPersonalInfoTab from "./ModalTabs/CustomerPersonalInfoTab";
import CustomerBillingTab from "./ModalTabs/CustomerBillingTab";
import CustomerAppointmentsTab from "./ModalTabs/CustomerAppointmentsTab";
import CustomerNotificationsTab from "./ModalTabs/CustomerNotificationsTab";
import {
	CustomerProfileDraft,
	buildCustomerOnlyUpdate,
	buildPatientRowUpdate,
	customerProfileDraft,
	hasPendingProfileChanges,
	isPatientDirectoryRow,
} from "../../../types/inventory/customerRecord";
import type { UserUuid } from "../../../types/identity";

/**
 * CU-86d44j2ac — the Customer drawer: separate from PatientModal and never clinical. A row's CURRENT
 * user_type routes its mutation: customer → generic admin user writer; patient → canonical patient update.
 */
interface CustomerDrawerProps {
	open: boolean;
	customer: Customer | null;
	onClose: () => void;
	onSaved: (customer: Customer) => void;
	onActivationChanged?: (customer: Customer) => void;
	/** CU-86d45b54n — resolves true only when the host actually reloaded; anything else reports no success. */
	onRefresh?: () => void | boolean | Promise<void | boolean>;
	refreshing?: boolean;
}

const CustomerDrawer: React.FC<CustomerDrawerProps> = ({
	open,
	customer,
	onClose,
	onSaved,
	onActivationChanged,
	onRefresh,
	refreshing = false,
}) => {
	const [activeTab, setActiveTab] = useState("overview");
	const [draft, setDraft] = useState<CustomerProfileDraft | null>(null);
	const [saving, setSaving] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const [passwordModalOpen, setPasswordModalOpen] = useState(false);
	const { showSuccess } = useNotifications();

	// CU-86d44j2ac: only a DIFFERENT record returns the drawer to Overview, so a save on Personal Info stays there.
	const openRecordIdRef = useRef<UserUuid | null>(null);
	// CU-86d44j2ac: the draft is rebuilt only for a different record or after an explicit Refresh, so an
	// auxiliary update (picture, Stripe, activation) replacing the record never discards unsaved edits.
	const adoptNextRecordRef = useRef(false);

	useEffect(() => {
		const recordId = customer?.id ?? null;
		const differentRecord = recordId !== openRecordIdRef.current;

		if (differentRecord) {
			openRecordIdRef.current = recordId;
			setActiveTab("overview");
		}

		setErrors([]);

		if (!customer) {
			setDraft(null);
			return;
		}

		if (differentRecord || adoptNextRecordRef.current) {
			adoptNextRecordRef.current = false;
			setDraft(customerProfileDraft(customer));
			return;
		}

		setDraft((current) =>
			current
				? { ...current, profile_picture: customer.profile_picture ? String(customer.profile_picture) : "" }
				: customerProfileDraft(customer),
		);
	}, [customer]);

	useEffect(() => {
		if (!refreshing) adoptNextRecordRef.current = false;
	}, [refreshing]);

	if (!customer || !draft) return null;

	const isPatientRow = isPatientDirectoryRow(customer);
	const hasUnsavedChanges = hasPendingProfileChanges(draft, customer);

	const handleFieldChange = (field: keyof CustomerProfileDraft, value: string | null) => {
		setDraft((current) => (current ? { ...current, [field]: value } : current));
	};

	const reportError = (error: unknown, fallback: string) => {
		if (axios.isAxiosError(error)) {
			const body = error.response?.data;
			const validationErrors = body?.errors ?? body;

			if (validationErrors && typeof validationErrors === "object") {
				const messages = Object.values(validationErrors)
					.flat()
					.filter((value): value is string => typeof value === "string");

				if (messages.length > 0) {
					setErrors(messages);
					return;
				}
			}

			const message = body?.message;
			if (typeof message === "string" && message.trim()) {
				setErrors([message]);
				return;
			}
		}

		setErrors([fallback]);
	};

	const save = async () => {
		setSaving(true);
		setErrors([]);

		try {
			if (isPatientRow) {
				await updatePatient(customer.id, buildPatientRowUpdate(draft) as never);
			} else {
				await updateUser(customer.id, buildCustomerOnlyUpdate(draft) as never);
			}

			showSuccess("Customer updated");

			onSaved({
				...customer,
				first_name: draft.first_name,
				middle_name: draft.middle_name,
				last_name: draft.last_name,
				username: draft.username,
				email: draft.email,
				phone: draft.phone,
				address: draft.address,
				city: draft.city,
				state: draft.state,
				country: draft.country,
				postal_code: draft.postal_code,
				date_of_birth: draft.date_of_birth ? new Date(draft.date_of_birth) : null,
				gender: draft.gender,
				sex: draft.sex,
				height: draft.height,
				weight: draft.weight,
				profile_picture: draft.profile_picture,
			});
		} catch (error) {
			reportError(error, "Failed to update the customer.");
		} finally {
			setSaving(false);
		}
	};

	const refresh = async () => {
		if (!onRefresh) return;

		adoptNextRecordRef.current = true;

		if ((await onRefresh()) === true) {
			showSuccess("Customer refreshed");
		}
	};

	const toggleActivation = async () => {
		const activating = customer.is_active === false;

		setSaving(true);
		setErrors([]);

		try {
			if (isPatientRow) {
				await (activating ? activatePatient(customer.id) : deactivatePatient(customer.id));
			} else {
				await (activating ? activateUser(customer.id) : deactivateUser(customer.id));
			}

			(onActivationChanged ?? onSaved)({ ...customer, is_active: activating });
		} catch (error) {
			reportError(
				error,
				activating ? "Failed to activate the customer." : "Failed to deactivate the customer.",
			);
		} finally {
			setSaving(false);
		}
	};

	const tabs = [
		{
			key: "overview",
			label: "Overview",
			children: (
				<CustomerOverviewTab
					customer={customer}
					onCustomerChange={onSaved}
					hasUnsavedChanges={hasUnsavedChanges}
				/>
			),
		},
		{
			key: "personal",
			label: "Personal Info",
			children: (
				<CustomerPersonalInfoTab
					customer={customer}
					draft={draft}
					onFieldChange={handleFieldChange}
				/>
			),
		},
		{
			key: "billing",
			label: "Billing",
			children: (
				<CustomerBillingTab
					customer={customer}
					draft={draft}
					onStripeCustomerIdChange={(stripeCustomerId) =>
						onSaved({ ...customer, stripe_customer_id: stripeCustomerId ?? null })
					}
				/>
			),
		},
		{
			key: "appointments",
			label: "Appointments",
			children: <CustomerAppointmentsTab customer={customer} />,
		},
		{
			key: "notifications",
			label: "Notifications",
			children: <CustomerNotificationsTab customer={customer} />,
		},
		{
			key: "orders",
			label: "Orders",
			children: (
				<OrdersTab
					patient={customer}
					emptyTitle="No orders found for this customer"
				/>
			),
		},
	];

	return (
		<>
			<DetailDrawer
				open={open}
				onClose={onClose}
				width="85vw"
				title=""
				titleMeta={<CustomerInfoStrip customer={customer} />}
				tabs={tabs}
				activeTab={activeTab}
				onTabChange={setActiveTab}
				tabLayout="sidebar"
				errorMessages={errors}
				onClearErrors={() => setErrors([])}
				headerActions={
					<>
						{onRefresh && (
							<Button
								icon={<ReloadOutlined />}
								onClick={() => void refresh()}
								loading={refreshing}
							>
								Refresh
							</Button>
						)}
						<Button
							type="primary"
							icon={<SaveOutlined />}
							onClick={save}
							loading={saving}
							disabled={!draft.email.trim()}
						>
							Save
						</Button>
					</>
				}
				footer={
					<>
						<Button
							icon={<LockOutlined />}
							onClick={() => setPasswordModalOpen(true)}
							disabled={saving}
						>
							Reset Password
						</Button>
						{activeTab === "personal" && (
							<Button
								danger={customer.is_active !== false}
								onClick={toggleActivation}
								loading={saving}
							>
								{customer.is_active === false ? "Activate" : "Deactivate"}
							</Button>
						)}
					</>
				}
			/>

			{passwordModalOpen && (
				<ResetPasswordModal
					isOpen={passwordModalOpen}
					onClose={() => setPasswordModalOpen(false)}
					user={customer}
					handleErrors={(error) => reportError(error, "Failed to reset the password.")}
				/>
			)}
		</>
	);
};

export default CustomerDrawer;
